import { useCallback, useEffect, useRef, useState } from 'react';

import { useLatest } from '../../../utils/use-latest';
import type { Framing } from './faceFraming';

export type CameraErrorKind =
  'insecure' | 'denied' | 'notFound' | 'busy' | 'unknown';

const IDLE_FRAMING: Framing = { ok: false, hint: 'noFace', crop: null };

const BASE_VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  width: { ideal: 1280 },
  height: { ideal: 960 },
  facingMode: 'user',
};

/**
 * Any property access on `localStorage` throws `SecurityError` when site data
 * is blocked for the origin (a Chrome cookie setting). A forgotten camera
 * preference is not worth taking the page down — least of all from inside a
 * `useState` initialiser, which runs during render and reaches the error
 * boundary.
 */
function readStoredDevice(key: string | undefined): string | null {
  if (!key) {
    return null;
  }
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Writes, or removes when `id` is null. Same SecurityError caveat as above. */
function writeStoredDevice(key: string | undefined, id: string | null): void {
  if (!key) {
    return;
  }
  try {
    if (id === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, id);
    }
  } catch {
    // Ignored on purpose: the camera choice simply does not survive a reload.
  }
}

function errorKindOf(err: unknown): CameraErrorKind {
  const name = err instanceof Error ? err.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'denied';
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return 'notFound';
  }
  if (name === 'NotReadableError' || name === 'AbortError') {
    return 'busy';
  }
  return 'unknown';
}

interface CameraStreamOptions {
  /** When set, the chosen camera is remembered under this localStorage key. */
  deviceStorageKey: string | undefined;
  onError: ((kind: CameraErrorKind) => void) | undefined;
}

/**
 * Owns the camera: opening the stream on the chosen device, the device list
 * behind the picker, the terminal error state and its retry, and releasing
 * the camera while the tab is hidden.
 *
 * `framing` lives here too, although the detection loop is what feeds it: it
 * belongs to one stream session, and every new session — and every hidden
 * tab — must drop it to idle in the same pass that drops `ready`.
 */
export function useCameraStream({
  deviceStorageKey,
  onError,
}: CameraStreamOptions) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [deviceId, setDeviceId] = useState<string | null>(() =>
    readStoredDevice(deviceStorageKey),
  );
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  /** Bumped to re-open the stream: retry after an error, return from a hidden tab. */
  const [restartNonce, setRestartNonce] = useState(0);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<CameraErrorKind | null>(null);
  const [framing, setFraming] = useState<Framing>(IDLE_FRAMING);

  // onError lives in a ref: a consumer passing an inline arrow would otherwise
  // change `fail`'s identity every render and reopen the camera in a loop.
  const onErrorRef = useLatest(onError);

  // Mirrors `error` for the visibility handler below, which is registered
  // once (empty deps, see that effect) and would otherwise see a stale value.
  const errorRef = useLatest(error);

  const fail = useCallback(
    (kind: CameraErrorKind) => {
      // A failure after the stream was already open (e.g. play() rejecting)
      // must not leave the camera light on behind the error UI.
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- fail() runs once on a terminal camera-open failure; the extra render pass is the point
      setError(kind);
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- fail() runs once on a terminal camera-open failure; the extra render pass is the point
      setReady(false);
      onErrorRef.current?.(kind);
      // Stable: useLatest returns the same ref every render.
    },
    [onErrorRef],
  );

  // Open the stream. Re-runs when the operator picks another camera, on
  // retry, and on return from a hidden tab.
  // Every setter below an await is gated on `run.signal.aborted` (or on
  // a predicate that reads it). react-doctor only recognises a `let` flag
  // flipped by the cleanup, which no-unnecessary-condition rejects because
  // narrowing cannot see the cleanup.
  // react-doctor-disable-next-line react-doctor/no-set-state-after-await-in-effect
  useEffect(() => {
    // A new run means a new session: none of the previous stream's detection
    // state may survive into it: a crop computed against a different camera's
    // geometry would let an operator capture against a session that no longer
    // exists.
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- this reset IS the effect's job: every run opens a new stream session and must not carry over the previous one's framing state
    setReady(false);
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- this reset IS the effect's job: every run opens a new stream session and must not carry over the previous one's framing state
    setFraming(IDLE_FRAMING);

    if (!window.isSecureContext) {
      fail('insecure');
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- absent in some embedded browsers even over https, whatever the DOM typings say
    if (!navigator.mediaDevices?.getUserMedia) {
      fail('notFound');
      return;
    }
    // An already-hidden tab gets no camera. `visibilitychange` only fires on
    // transitions, so a mount that happens while hidden would otherwise open
    // anyway; the visibility effect bumps `restartNonce` on becoming visible,
    // which re-runs this effect and opens it then.
    if (document.visibilityState === 'hidden') {
      return;
    }

    // Aborted by this effect's cleanup; a plain `let` flag reads as always
    // false to control-flow narrowing, which cannot see the cleanup.
    const run = new AbortController();
    void (async () => {
      try {
        let stream: MediaStream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: deviceId
              ? { ...BASE_VIDEO_CONSTRAINTS, deviceId: { exact: deviceId } }
              : BASE_VIDEO_CONSTRAINTS,
            audio: false,
          });
        } catch (err) {
          // Torn down while the first attempt was pending: the run that
          // replaced this one owns the device choice and the retry.
          if (run.signal.aborted) {
            return;
          }
          // A remembered device id goes stale when a camera is unplugged,
          // permissions are reset, or the profile moves machine — an exact
          // constraint on it throws, which must not be reported as "no
          // camera" while one is plugged in. Matters most for the kiosk,
          // the one consumer that persists a deviceId across sessions.
          // Clearing `deviceId` re-runs this effect, and that run opens the
          // default camera: opening it here as well would only be aborted by
          // that same re-run, for a third getUserMedia call.
          if (deviceId && errorKindOf(err) === 'notFound') {
            writeStoredDevice(deviceStorageKey, null);
            setDeviceId(null);
            return;
          }
          throw err;
        }

        if (run.signal.aborted || document.visibilityState === 'hidden') {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        if (!videoRef.current) {
          // Nowhere to attach the stream — the error view unmounts <video>,
          // and a stream should never resolve into that state, but if one
          // does, don't claim to be ready over a black rectangle.
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        videoRef.current.srcObject = stream;
        // `run.signal.aborted` only flips from this effect's own cleanup — it can't
        // see the separate visibility effect nulling `srcObject` on this
        // same element while this await is pending, which aborts the
        // pending play() with AbortError. That is teardown, not a camera
        // fault, and any of these three signals proves it: the effect was
        // torn down, the tab went hidden, or something reassigned
        // `srcObject` out from under this attempt. A real AbortError from
        // an actual camera fault cannot show any of them — the element is
        // still showing our own live session — so it still propagates.
        const supersededDuring = () =>
          run.signal.aborted ||
          document.visibilityState === 'hidden' ||
          videoRef.current?.srcObject !== stream;
        try {
          await videoRef.current.play();
        } catch (err) {
          if (!supersededDuring()) {
            throw err;
          }
        }
        if (supersededDuring()) {
          return;
        }
        setError(null);
        setReady(true);
      } catch (err) {
        if (!run.signal.aborted) {
          fail(errorKindOf(err));
        }
        return;
      }
      // Labels are only populated once permission has been granted. This
      // only feeds the device picker — its failure must not tear down an
      // already-working preview, so it gets its own try and is ignored.
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- narrowed to false by the early return above, but the cleanup can abort during this await
        if (!run.signal.aborted) {
          setDevices(all.filter((d) => d.kind === 'videoinput'));
        }
      } catch {
        // Ignored on purpose: the picker simply does not render.
      }
    })();

    return () => {
      run.abort();
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, [deviceId, restartNonce, fail, deviceStorageKey]);

  // A hidden tab keeps the camera light on for nothing. Reopen on return —
  // without the nonce the open effect never re-runs and the camera stays
  // dead — unless an error is showing: the <video> is unmounted then, so a
  // stream that resolved would have nowhere to attach, and the retry button
  // is the one intended path back from an error.
  useEffect(() => {
    function onVisibility() {
      if (document.visibilityState === 'hidden') {
        streamRef.current?.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        if (videoRef.current) {
          videoRef.current.srcObject = null;
        }
        setReady(false);
        setFraming(IDLE_FRAMING);
      } else if (!errorRef.current) {
        setRestartNonce((n) => n + 1);
      }
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // Stable: useLatest returns the same ref every render.
  }, [errorRef]);

  function selectDevice(id: string) {
    writeStoredDevice(deviceStorageKey, id);
    setDeviceId(id);
  }

  function retry() {
    setError(null);
    setRestartNonce((n) => n + 1);
  }

  const selectedDeviceId =
    deviceId && devices.some((d) => d.deviceId === deviceId)
      ? deviceId
      : devices[0]?.deviceId;

  return {
    videoRef,
    ready,
    error,
    framing,
    setFraming,
    devices,
    selectedDeviceId,
    selectDevice,
    retry,
  };
}
